import { useState, useEffect } from "react";
import apiFetch from "@wordpress/api-fetch";
import { __ } from "@wordpress/i18n";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "./UserRoleEditor.scss";
import ContentHeader from "../../components/ContentHeader";

import ArrowDown from "../../assets/images/ArrowDown.svg";
import ArrowUp from "../../assets/images/ArrowUp.svg";

const UserRoleEditor = () => {
  // State management
  const [roles, setRoles] = useState({});
  const [selectedRole, setSelectedRole] = useState("");
  const [capabilities, setCapabilities] = useState([]);
  const [roleCapabilities, setRoleCapabilities] = useState([]);
  const [originalRoleCapabilities, setOriginalRoleCapabilities] = useState([]);
  const [capabilityGroups, setCapabilityGroups] = useState({});
  const [selectedGroup, setSelectedGroup] = useState("All");
  const [showDeprecated, setShowDeprecated] = useState(true);
  const [activeAll, setActiveAll] = useState(false);
  const [filteredCapabilities, setFilteredCapabilities] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({
    Core: true,
    "Custom Post Types": false,
    "Custom capabilities": false,
    WooCommerce: false,
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [allCapabilities, setAllCapabilities] = useState([]); // Store all capabilities
  const [loading, setLoading] = useState(true);


  // Group definitions for category organization
  const groupStructure = {
    Core: [
      // "Core",
      "General",
      "Themes",
      "Posts",
      "Pages",
      "Plugins",
      "Users",
      "Deprecated",
    ],
    "Custom capabilities": ["Custom capabilities", "User Role Editor"],
    WooCommerce: [
      "WooCommerce",
      "Products",
      "Orders",
      "Coupons",
      "Variations",
      "Refunds",
    ],
  };

  // Load initial data
  useEffect(() => {
    loadRoles();
    loadAllCapabilities();
    loadCapabilityGroups();
  }, []);

  // Select first role when roles are loaded
  useEffect(() => {
    if (Object.keys(roles).length > 0) {
      const defaultRole = Object.keys(roles)[0];
      setSelectedRole(defaultRole);
      loadRoleCapabilities(defaultRole);
    }
  }, [roles]);

  // Update filtered capabilities when selection changes
  useEffect(() => {
    filterCapabilities();
    updateActiveAllState();
  }, [selectedGroup, allCapabilities, capabilityGroups, showDeprecated, roleCapabilities]);

  // Check for unsaved changes
  useEffect(() => {
    if (originalRoleCapabilities.length > 0) {
      // Check if arrays are different
      const hasChanges =
        roleCapabilities.length !== originalRoleCapabilities.length ||
        !roleCapabilities.every((cap) =>
          originalRoleCapabilities.includes(cap)
        ) ||
        !originalRoleCapabilities.every((cap) =>
          roleCapabilities.includes(cap)
        );

      setHasUnsavedChanges(hasChanges);
    }
  }, [roleCapabilities, originalRoleCapabilities]);

  const loadRoles = async () => {
    try {
      const response = await apiFetch({ path: "/plac/v1/roles" });
      setRoles(response.roles);
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  };

  const loadCapabilityGroups = async () => {
    try {
      const response = await apiFetch({
        path: "/plac/v1/capability-groups",
      });
      setCapabilityGroups(response || {});
    } catch (error) {
      console.error("Error loading capability groups:", error);
      setCapabilityGroups({}); // Set default empty object
    }
  };

  const loadAllCapabilities = async () => {
    try {
      setLoading(true);
      const response = await apiFetch({ path: "/plac/v1/all-capabilities" });
  
      // Ensure response is an object and contains a capabilities array
      if (response && Array.isArray(response.capabilities)) {
        setAllCapabilities(response.capabilities); // Store all capabilities in the system
        setCapabilities(response.capabilities);
        setLoading(false);
      } else {
        console.error("Invalid response format:", response);
        setAllCapabilities([]); // Set an empty array as fallback
        setCapabilities([]);
      }
    } catch (error) {
      console.error("Error loading capabilities:", error);
      setAllCapabilities([]); // Ensure state is always an array
      setCapabilities([]);
      setLoading(false);
    }
  };
  
  const loadRoleCapabilities = async (role) => {
    setSelectedRole(role);
    try {
      const response = await apiFetch({
        path: "/plac/v1/role-capabilities",
        method: "POST",
        data: { role },
      });
      
      
      // Set the role capabilities with a fallback to empty array
      setRoleCapabilities(response.capabilities || []);
      setOriginalRoleCapabilities([...(response.capabilities || [])]);
      setHasUnsavedChanges(false);
      
      // Check if role is read-only (e.g., administrator)
      setIsReadOnly(response.readonly === true);
    } catch (error) {
      console.error("Error loading role capabilities:", error);
    }
  };

  // Helper function to get filtered capabilities for a specific group
  const getFilteredCapabilitiesForGroup = (group) => {
    let filtered = [...allCapabilities];
    
    // Filter by group if not "All"
    if (group !== "All") {
      filtered = filtered.filter((cap) => {
        return capabilityGroups[group]?.includes(cap);
      });
    }
    
    // Apply deprecated filter if needed
    if (!showDeprecated) {
      const deprecatedCaps = [
        "level_0", "level_1", "level_2", "level_3", "level_4",
        "level_5", "level_6", "level_7", "level_8", "level_9", 
        "level_10", "edit_files",
      ];
      filtered = filtered.filter((cap) => !deprecatedCaps.includes(cap));
    }
    
    return filtered;
  };

  const updateActiveAllState = () => {
    // Only consider active if there are filtered capabilities and ALL of them are in roleCapabilities
    const allActive = 
      filteredCapabilities.length > 0 && 
      filteredCapabilities.every((cap) => roleCapabilities.includes(cap));
    
    setActiveAll(allActive);
  };

  const filterCapabilities = () => {
    const filtered = getFilteredCapabilitiesForGroup(selectedGroup);
    setFilteredCapabilities(filtered);
  };

  const handleSelectGroup = (group) => {
    // Calculate new filtered capabilities for the selected group
    const newFilteredCaps = getFilteredCapabilitiesForGroup(group);
    
    // Calculate if all capabilities in the new group are active
    const newAllActive = 
      newFilteredCaps.length > 0 && 
      newFilteredCaps.every((cap) => roleCapabilities.includes(cap));
    
    // Update states
    setSelectedGroup(group);
    setFilteredCapabilities(newFilteredCaps);
    setActiveAll(newAllActive);
  };

  const toggleGroupExpand = (group) => {
    setExpandedGroups({
      ...expandedGroups,
      [group]: !expandedGroups[group],
    });
  };

  const toggleCapability = (capability) => {
    if (isReadOnly) return; // Prevent changes if role is read-only
    
    const newCapabilities = [...roleCapabilities];

    if (newCapabilities.includes(capability)) {
      // Remove capability
      const index = newCapabilities.indexOf(capability);
      newCapabilities.splice(index, 1);
    } else {
      // Add capability
      newCapabilities.push(capability);
    }

    setRoleCapabilities(newCapabilities);
    
    // After updating capabilities, check if we need to update the activeAll state
    const allActive = 
      filteredCapabilities.length > 0 && 
      filteredCapabilities.every((cap) => 
        cap === capability ? !newCapabilities.includes(capability) : newCapabilities.includes(cap)
      );
    
    setActiveAll(allActive);
  };

  const toggleAllCapabilities = () => {
    if (isReadOnly) return; // Prevent changes if role is read-only
    
    const newState = !activeAll;
    
    if (newState) {
      // Activate all capabilities in the current filtered view
      const newRoleCaps = [...new Set([...roleCapabilities, ...filteredCapabilities])];
      setRoleCapabilities(newRoleCaps);
    } else {
      // Deactivate all capabilities in the current filtered view
      const newRoleCaps = roleCapabilities.filter(
        (cap) => !filteredCapabilities.includes(cap)
      );
      setRoleCapabilities(newRoleCaps);
    }
    
    // Set activeAll state to match the action we just took
    setActiveAll(newState);
  };

  // Save changes
  const saveChanges = async () => {
    if (!hasUnsavedChanges || isReadOnly) return;

    setIsSaving(true);
    try {
      const response = await apiFetch({
        path: "/plac/v1/update-capabilities",
        method: "POST",
        data: {
          _wpnonce: window.placAdminData.nonce,
          role: selectedRole,
          capabilities: roleCapabilities,
        },
      });

      if (response.success) {
        // Update original state to match current state
        setOriginalRoleCapabilities([...roleCapabilities]);
        setHasUnsavedChanges(false);
        
        // Show success message for 1.5s
        setTimeout(() => {
          setIsSaving(false);
          setIsSaved(true);

          // Reset success message after 1.5s
          setTimeout(() => {
            setIsSaved(false);
          }, 1500);
        }, 500);
      }
    } catch (error) {
      console.error("Error saving capabilities:", error);
      setIsSaving(false);
    }
  };

  // Discard changes
  const discardChanges = () => {
    if (!hasUnsavedChanges || isReadOnly) return;

    // Restore original capabilities
    setRoleCapabilities([...originalRoleCapabilities]);
    setHasUnsavedChanges(false);
    
    // Update activeAll state based on restored capabilities
    const allActive = 
      filteredCapabilities.length > 0 && 
      filteredCapabilities.every((cap) => originalRoleCapabilities.includes(cap));
    
    setActiveAll(allActive);
  };

  // Get total capabilities for a main group (including all subgroups)
  const getTotalGroupCapabilities = (mainGroup) => {
    // If the main group is not in the structure, return 0
    if (!groupStructure[mainGroup]) return 0;

    // Get all subgroups
    const subgroups = groupStructure[mainGroup];

    // Get unique capabilities across all subgroups
    const allCaps = new Set();
    subgroups.forEach((subgroup) => {
      const subgroupKey = Object.keys(capabilityGroups).find((key) =>
        key.startsWith(subgroup)
      );

      if (subgroupKey && capabilityGroups[subgroupKey]) {
        capabilityGroups[subgroupKey].forEach((cap) => allCaps.add(cap));
      }
    });

    return allCaps.size;
  };

  // Get granted capabilities for a main group
  const getGrantedGroupCapabilities = (mainGroup) => {
    // If the main group is not in the structure, return 0
    if (!groupStructure[mainGroup]) return 0;

    // Get all subgroups
    const subgroups = groupStructure[mainGroup];

    // Count granted capabilities across all subgroups
    let grantedCount = 0;
    const checkedCaps = new Set();

    subgroups.forEach((subgroup) => {
      const subgroupKey = Object.keys(capabilityGroups).find((key) =>
        key.startsWith(subgroup)
      );

      if (subgroupKey && capabilityGroups[subgroupKey]) {
        capabilityGroups[subgroupKey].forEach((cap) => {
          if (roleCapabilities.includes(cap) && !checkedCaps.has(cap)) {
            grantedCount++;
            checkedCaps.add(cap);
          }
        });
      }
    });

    return grantedCount;
  };

  // Get count display for a specific group
  const getGroupCounts = (group) => {
    if (group === "All") {
      return `(${allCapabilities.length}/${roleCapabilities.length})`;
    }

    // For main groups
    if (groupStructure[group]) {
      const totalCount = getTotalGroupCapabilities(group);
      const grantedCount = getGrantedGroupCapabilities(group);
      return `(${totalCount}/${grantedCount})`;
    }

    // For subgroups
    const groupName = group.split(" (")[0];
    const groupCaps = capabilityGroups[group] || [];
    const grantedCount = groupCaps.filter((cap) =>
      roleCapabilities.includes(cap)
    ).length;

    return `(${groupCaps.length}/${grantedCount})`;
  };

  return (
    <>
      {loading && (
        <div className="loading-container">
          <div className="text-center p-4">{__("Loading...", "access-control")}</div>
        </div>
      )}  
      <div className="settings-container">
        <ContentHeader
          title={"User Role Editor"}
          description={
            "Edit user roles and capabilities to manage access control for your WordPress site."
          }
        />

        <div className="role-management">
          <div className="role-header">
            <div className="select-role">
              <label>{__("Select Role and change its capabilities:", "access-control")}</label>
              <div className="select-wrapper">
                <select
                  onChange={(e) => loadRoleCapabilities(e.target.value)}
                  value={selectedRole}
                >
                  {Object.entries(roles).map(([key, name]) => (
                    <option key={key} value={key}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="toggle-option">
              <div className="deprecated-cap">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="show-depricated-cap-tooltip">
                      {__("Enable to show deprecated capabilities.", "access-control")}
                    </Tooltip>
                  }
                >
                  <img
                    src={`${window.placAdminData?.pluginUrl}/assets/images/question.svg`}
                    alt="icon"
                  />
                </OverlayTrigger>
                {__("Show deprecated capabilities", "access-control")}
              </div>
              <label className="plac-switch">
                <input
                  type="checkbox"
                  checked={showDeprecated}
                  onChange={() => {
                    const newShowDeprecated = !showDeprecated;
                    setShowDeprecated(newShowDeprecated);
                    
                    // Recalculate activeAll for the current group with the new setting
                    const newFilteredCaps = getFilteredCapabilitiesForGroup(selectedGroup);
                    const newAllActive = 
                      newFilteredCaps.length > 0 && 
                      newFilteredCaps.every((cap) => roleCapabilities.includes(cap));
                    
                    setActiveAll(newAllActive);
                  }}
                />
                <span className="plac-slider round"></span>
              </label>
            </div>
          </div>

          {isReadOnly && (
            <div className="readonly-warning">
              <p className="warning-text">
                {__("This role cannot be modified for security reasons.", "access-control")}
              </p>
            </div>
          )}

          <div className="role-content">
            <div className="role-sidebar">
              <div className="sidebar-header">
                <span>{__("Group (Total/Granted)", "access-control")}</span>
              </div>
              <div className="quick-filter">
                <div className="sidebar-menu">
                  <div className="sidebar-dropdown">
                    {/* All capabilities option */}
                    <div
                      className={`dropdown-item ${selectedGroup === "All" ? "active" : ""}`}
                      onClick={() => handleSelectGroup("All")}
                      style={selectedGroup === "All" ? { backgroundColor: "#720EEC", color: "white" } : {}}
                    >
                      All {getGroupCounts("All")}
                    </div>

                    {/* Core capabilities group */}
                    <div className="dropdown-group">
                      <div
                        className={`dropdown-heading ${
                          expandedGroups["Core"] ? "expanded" : ""
                        }`}
                        onClick={() => toggleGroupExpand("Core")}
                      >
                        <span>Core {getGroupCounts("Core")}</span>
                        <i
                          className={`dropdown-arrow ${
                            expandedGroups["Core"] ? "up" : "down"
                          }`}
                        >
                          {expandedGroups["Core"] ? <img src={ArrowUp} alt="icon" /> : <img src={ArrowDown} alt="icon" />}
                        </i>
                      </div>
                      {expandedGroups["Core"] &&
                        groupStructure["Core"].map((subgroup) => {
                          const fullGroupName = Object.keys(
                            capabilityGroups
                          ).find((key) => key.startsWith(subgroup));
                          if (!fullGroupName) return null;

                          // Get the specific counts for this subgroup
                          const subgroupCounts = getGroupCounts(fullGroupName);

                          return (
                            <div
                              key={fullGroupName}
                              className={`dropdown-subitem ${
                                selectedGroup === fullGroupName ? "active" : ""
                              }`}
                              onClick={() => handleSelectGroup(fullGroupName)}
                              style={selectedGroup === fullGroupName ? { backgroundColor: "#720EEC", color: "white" } : {}}
                            >
                              - {fullGroupName.split(" (")[0]} {subgroupCounts}
                            </div>
                          );
                        })}
                    </div>

                    {/* Custom capabilities group */}
                    <div className="dropdown-group">
                      <div
                        className={`dropdown-heading ${
                          expandedGroups["Custom capabilities"] ? "expanded" : ""
                        }`}
                        onClick={() => toggleGroupExpand("Custom capabilities")}
                      >
                        <span>
                          Custom capabilities{" "}
                          {getGroupCounts("Custom capabilities")}
                        </span>
                        <i
                          className={`dropdown-arrow ${
                            expandedGroups["Custom capabilities"]
                              ? "up"
                              : "down"
                          }`}
                        >
                          {expandedGroups["Custom capabilities"] ? (
                            <img src={ArrowUp} alt="icon"/>
                          ) : (
                            <img src={ArrowDown} alt="icon"/>
                          )}
                        </i>
                      </div>
                      {expandedGroups["Custom capabilities"] &&
                        groupStructure["Custom capabilities"].map(
                          (subgroup) => {
                            const fullGroupName = Object.keys(
                              capabilityGroups
                            ).find((key) => key.startsWith(subgroup));
                            if (!fullGroupName) return null;

                            // Get the specific counts for this subgroup
                            const subgroupCounts = getGroupCounts(fullGroupName);

                            return (
                              <div
                                key={fullGroupName}
                                className={`dropdown-subitem ${
                                  selectedGroup === fullGroupName
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() => handleSelectGroup(fullGroupName)}
                                style={selectedGroup === fullGroupName ? { backgroundColor: "#720EEC", color: "white" } : {}}
                              >
                                - {fullGroupName.split(" (")[0]} {subgroupCounts}
                              </div>
                            );
                          }
                        )}
                    </div>

                    {/* WooCommerce group */}
                    <div className="dropdown-group">
                      <div
                        className={`dropdown-heading ${
                          expandedGroups["WooCommerce"] ? "expanded" : ""
                        }`}
                        onClick={() => toggleGroupExpand("WooCommerce")}
                      >
                        <span>
                          WooCommerce {getGroupCounts("WooCommerce")}
                        </span>
                        <i
                          className={`dropdown-arrow ${
                            expandedGroups["WooCommerce"] ? "up" : "down"
                          }`}
                        >
                          {expandedGroups["WooCommerce"] ? (
                            <img src={ArrowUp} alt="icon"/>
                          ) : (
                            <img src={ArrowDown} alt="icon"/>
                          )}
                        </i>
                      </div>
                      {expandedGroups["WooCommerce"] &&
                        groupStructure["WooCommerce"].map((subgroup) => {
                          const fullGroupName = Object.keys(
                            capabilityGroups
                          ).find((key) => key.startsWith(subgroup));
                          if (!fullGroupName) return null;

                          // Get the specific counts for this subgroup
                          const subgroupCounts = getGroupCounts(fullGroupName);

                          return (
                            <div
                              key={fullGroupName}
                              className={`dropdown-subitem ${
                                selectedGroup === fullGroupName ? "active" : ""
                              }`}
                              onClick={() => handleSelectGroup(fullGroupName)}
                              style={selectedGroup === fullGroupName ? { backgroundColor: "#720EEC", color: "white" } : {}}
                            >
                              - {fullGroupName.split(" (")[0]} {subgroupCounts}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="capabilities-wrapper">
              <div className="capabilities-header">
                <div className="toggle-option">
                  <span>{__("Active all", "access-control")}</span>
                  <label className={`plac-switch ${isReadOnly ? "disabled" : ""}`}>
                    <input
                      type="checkbox"
                      checked={activeAll}
                      onChange={toggleAllCapabilities}
                      disabled={isReadOnly}
                    />
                    <span className="plac-slider round"></span>
                  </label>
                </div>
              </div>

              <div className="capabilities-grid">
                {filteredCapabilities.map((capability, index) => (
                  <div key={`${capability}-${index}`} className="capability-item">
                    <input
                      type="checkbox"
                      checked={roleCapabilities.includes(capability)}
                      id={`cap-${index}`}
                      onChange={() => toggleCapability(capability)}
                      disabled={isReadOnly}
                    />
                    <label htmlFor={`cap-${index}`}>
                      {capability}
                    </label>
                  </div>
                ))}
              </div>

              {filteredCapabilities.length === 0 && (
                <div className="no-capabilities">
                  <p>{__("No capabilities found in this group.", "access-control")}</p>
                </div>
              )}

              <div className="capabilities-footer"></div>
            </div>
          </div>

          {/* Footer with Save and Discard buttons */}
          <div className="footer-section" style={{padding: "0px"}}>
            <button
              className="button button-primary"
              disabled={!hasUnsavedChanges || isSaving || isReadOnly}
              onClick={saveChanges}
            >
              { (
                __("Save Changes", "access-control")
              )}
            </button>
            <button
              className="button button-secondary"
              disabled={!hasUnsavedChanges || isReadOnly}
              onClick={discardChanges}
            >
              {__("Discard Changes", "access-control")}
            </button>

            {hasUnsavedChanges && (
             <div className="unsaved-changes-indicator">
                  <span className="text-warning">
                      <i className="dashicons dashicons-warning"></i> {__("Unsaved changes", "access-control")}
                  </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserRoleEditor;
